import { useContext } from 'react';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { useCopyToClipboard } from '@confluence/copy-content-link/entry-points/useCopyToClipboard.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';

import type { AnalyticsOrigin } from '../../constants/snippetAnalytics.ts';
import { ANALYTICS_UI_SUBJECT_ID } from '../../constants/snippetAnalytics.ts';
import { DeleteSnippetModal } from '../../modals/DeleteSnippetModal.tsx';
import { DuplicateSnippetModal } from '../../modals/DuplicateSnippetModal.tsx';
import { EditSnippetModal } from '../../modals/EditSnippetModal.tsx';
import { PrivateTemplateCopyLinkModal } from '../../modals/PrivateTemplateCopyLinkModal.tsx';
import type { Snippet } from '../../types/snippet.ts';
import { getTemplateGallerySnippetUrl } from '../../utils/templateGalleryUrl.ts';
import { useSnippetAnalytics } from '../analytics/useSnippetAnalytics.ts';

export type SnippetActionHandlersOptions = {
	analyticsOrigin: AnalyticsOrigin;
	contentId?: string;
	spaceId?: string;
	spaceKey?: string;
};

export const useSnippetActionHandlers = ({
	analyticsOrigin,
	contentId = '',
	spaceId = '',
	spaceKey = '',
}: SnippetActionHandlersOptions) => {
	const { fireButtonClickedAnalytic } = useSnippetAnalytics(analyticsOrigin);
	const { showModal } = useDialogs();
	const { copyToClipboard } = useCopyToClipboard();
	const routeDataRef = useRouteDataRef();
	const { editorAPI } = useContext(EditorContextPanelContext);

	const getEffectiveSpaceKey = (spaceKeyProp: string): string => {
		if (spaceKeyProp && spaceKeyProp.trim()) {
			return spaceKeyProp.trim();
		}
		const routeSpace = routeDataRef.current?.queryParams?.space;
		const spaceFromRoute =
			typeof routeSpace === 'string'
				? routeSpace
				: Array.isArray(routeSpace)
					? (routeSpace[0] ?? '')
					: '';
		return spaceKey || spaceFromRoute;
	};

	const handleEdit = (snippet: Snippet) => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.EDIT_SNIPPET, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
		});
		showModal(EditSnippetModal, {
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
		});
	};

	const handleDuplicate = (snippet: Snippet) => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.DUPLICATE_SNIPPET, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
		});
		showModal(DuplicateSnippetModal, {
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
			analyticsOrigin,
		});
	};

	const handleDelete = (snippet: Snippet) => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.DELETE_SNIPPET, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
		});
		showModal(DeleteSnippetModal, { snippet, editorAPI });
	};

	const handleCopyLink = async (snippet: Snippet): Promise<boolean> => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.COPY_SNIPPET_LINK, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
			isShared: snippet.isShared,
		});

		const isPersonalPrivate = snippet.isShared === false;

		if (isPersonalPrivate) {
			showModal(PrivateTemplateCopyLinkModal, {
				snippet,
				editorAPI,
				contentId,
				spaceId,
				spaceKey,
				analyticsOrigin,
			});
			return false;
		}

		const effectiveSpaceKey = getEffectiveSpaceKey(spaceKey);
		const path = getTemplateGallerySnippetUrl(snippet.snippetId, effectiveSpaceKey);
		const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${path}` : path;
		try {
			await copyToClipboard(fullUrl);
			return true;
		} catch {
			return false;
		}
	};

	return { handleEdit, handleDuplicate, handleDelete, handleCopyLink };
};
